/// <reference types="@types/dom-navigation" />
import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { encodeFile } from './encodeFile.js';
import styles from './styles.js';
import './bundid-element.js';

import type { BundidAntragRequestBody, UnicornAppData } from '../types/types.js';
import type { BundidElement } from './bundid-element.js';
import type { Ref } from 'lit/directives/ref.js';

/**
 *
 */
@customElement('unicorn-element')
export class BundidAntrag extends LitElement {
    static styles = [
        unsafeCSS(styles),
        css``,
    ];

    @state()
    dataComplete : boolean = false;

    @state()
    dataSubmitted : boolean = false;

    @state()
    dataSubmissionInProgress : boolean = false;

    @state()
    unicornPhotoPreviewUrl : string | undefined;

    bundidElementRef : Ref<BundidElement> = createRef<BundidElement>();

    data : UnicornAppData = {};

    /**
     *
     */
    checkDataComplete() : boolean {
        return (
            !!this.data.givenName &&
            !!this.data.surname &&
            !!this.data.postalAddress &&
            !!this.data.localityName &&
            !!this.data.postalCode && !!this.data.country &&
            !!this.data.unicornBirthdate &&
            !!this.data.unicornPhoto
        );
    }

    /**
     *
     * @param fieldName
     * @param event
     */
    updateData(fieldName : keyof UnicornAppData, event : Event) {
        if (fieldName === 'unicornPhoto') {
            const newUnicornPhotoFile = (event.target as HTMLInputElement).files?.[0];
            this.data[fieldName] = newUnicornPhotoFile;
            if (this.unicornPhotoPreviewUrl){
                URL.revokeObjectURL(this.unicornPhotoPreviewUrl);
                this.unicornPhotoPreviewUrl = undefined;
            }
            if (newUnicornPhotoFile) this.unicornPhotoPreviewUrl = URL.createObjectURL(newUnicornPhotoFile);
        } else if (fieldName === 'unicornRelatedPDF') {
            this.data[fieldName] = (event.target as HTMLInputElement).files?.[0];
            // No preview generation required
        } else this.data[fieldName] = (event.target as HTMLInputElement).value || undefined;
        this.dataComplete = this.checkDataComplete();
        if (this.dataComplete) console.log('Data complete');
    }

    /**
     *
     */
    updateIdToken(event : Event){
        if (this.bundidElementRef?.value?.keycloak?.idTokenParsed) if (
            event.type === 'idtokenupdate' &&
            this.bundidElementRef.value &&
            this.bundidElementRef.value.keycloak?.idTokenParsed &&
            Object.keys(this.bundidElementRef.value.keycloak?.idTokenParsed).length > 0
        ) {
            console.log('Updating UnicornApp data with fresh BundID data ...');
            const oldData : UnicornAppData = this.data;
            this.data = this.bundidElementRef.value.keycloak.idTokenParsed as UnicornAppData;
            this.data.unicornName = oldData.unicornName;
            this.data.unicornBirthdate = oldData.unicornBirthdate;
            this.data.unicornPhoto = oldData.unicornPhoto;
            this.requestUpdate('data');
        } else console.warn('"idtokenupdate" event received, but token data seems to be invalid or empty.');
        else {
            console.log('IdToken not available yet. Waiting for it ...');
            setTimeout(this.updateIdToken.bind(this), 100, event);
        }
    }

    /**
     *
     */
    async submitDataToBackend(){
        this.dataSubmissionInProgress = true;
        const unicornPhotoBase64 = await encodeFile(this.data.unicornPhoto);
        const unicornPDFBase64 = await encodeFile(this.data.unicornRelatedPDF);
        if (this.bundidElementRef.value?.keycloak)
            try {
                console.log('Checking id_token freshness ...');
                await this.bundidElementRef.value?.keycloak.updateToken(60);
            } catch(error) { console.error('Failed to refresh token:', error); }
        const dataToSubmit : BundidAntragRequestBody = {
            applicationName: '🦄',
            data: this.data,
            idToken: this.bundidElementRef.value?.keycloak?.idToken || '',
            unicornPhotoBase64: unicornPhotoBase64,
            unicornPDFBase64: unicornPDFBase64,
        };
        console.log('Posting data to backend service ...');
        try {
            const response = await fetch('https://bundid.spreewunder.de/bundid-antrag', {
                method: 'POST',
                cache: 'no-cache',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(dataToSubmit),
            });
            this.dataSubmitted = true;
            console.log(response);
        } catch(error) { console.warn('Failed: ', error); }
        this.dataSubmissionInProgress = false;
    }

    /**
     *
     */
    render() {
        return html`
            <div xmlns="http://www.w3.org/1999/html">
                <bundid-element @idtokenupdate=${this.updateIdToken} ${ref(this.bundidElementRef)}></bundid-element>
                <div class="prose mt-6">
                    <h3>Deklarationen zur Entrichtung von Einhornsteuer (§15 EinhStG)</h3>
                    <p>Machen Sie im Folgenden bitte Angaben zu den von Ihnen gehaltenen Einhörnern. Diese können mit
                        diesem Online-Formular direkt an die zuständige Gemeinde übermittelt werden. Wenn Sie Ihre
                        persönlichen Daten mittels BundID und einem elektronischen Ausweisdokument bereitstellen, kann
                        Ihr Antrag ohne weitere Identitätsprüfung schnell bearbeitet werden. Zudem erhalten Sie in
                        diesem Fall eine Bestätigung der Übermittlung Ihrer Angaben sowie alle weiteren Informationen
                        zum Vorgang direkt als elektronische Nachrichten in Ihr BundID-Postfach.</p>
                    <h4>Angaben zu Ihrer Person</h4>
                    <div class="join join-vertical">
                        <label class="input">
                            <span class="label">Vorname(n) *</span>
                            <input @change=${this.updateData.bind(this, 'givenName')} required type="text"
                                   .value=${this.data.givenName || nothing} placeholder="Vorname"/>
                        </label>
                        <label class="input">
                            <span class="label">Nachname *</span>
                            <input @change=${this.updateData.bind(this, 'surname')} required type="text"
                                   .value=${this.data.surname || nothing} placeholder="Nachname"/>
                        </label>
                        <label class="input">
                            <span class="label">Straße *</span>
                            <input @change=${this.updateData.bind(this, 'postalAddress')} required type="text"
                                   .value=${this.data.postalAddress || nothing} placeholder="Anschrift (Straße)"/>
                        </label>
                        <label class="input">
                            <span class="label">Ort *</span>
                            <input @change=${this.updateData.bind(this, 'localityName')} required type="text"
                                   .value=${this.data.localityName || nothing} placeholder="Anschrift (Ort)"/>
                        </label>
                        <label class="input">
                            <span class="label">PLZ *</span>
                            <input @change=${this.updateData.bind(this, 'postalCode')} required type="text"
                                   .value=${this.data.postalCode || nothing} placeholder="Anschrift (Postleitzahl)"/>
                        </label>
                        <label class="input">
                            <span class="label">Land *</span>
                            <input @change=${this.updateData.bind(this, 'country')} required type="text"
                                   .value=${this.data.country || nothing} placeholder="Anschrift (Land)"/>
                        </label>
                        <label class="input">
                            <span class="label">Telefonnummer</span>
                            <input @change=${this.updateData.bind(this, 'telephoneNumber')} type="text"
                                   .value=${this.data.telephoneNumber || nothing} placeholder="+49 30 123456789"/>
                        </label>
                    </div>
                    <h4>Angaben zum Einhorn</h4>
                    <div class="join join-vertical">
                        <label class="input">
                            <span class="label">Name</span>
                            <input @change=${this.updateData.bind(this, 'unicornName')} type="text"
                                   .value=${this.data.unicornName || nothing} placeholder="Name Ihres Einhorns"/>
                        </label>
                        <label class="input">
                            <span class="label">Geburtsdatum *</span>
                            <input @change=${this.updateData.bind(this, 'unicornBirthdate')} required type="date"
                                   .value=${this.data.unicornBirthdate || nothing}
                                   placeholder="Geburtsdatum Ihres Einhorns"/>
                        </label>
                        <fieldset class="fieldset">
                            <input @change=${this.updateData.bind(this, 'unicornPhoto')} required type="file"
                                   accept=".jpg,.png,.webp,.avif,.svg,image/jpeg,image/png,image/webp,image/avif,image/svg+xml"
                                   class="file-input"/>
                            ${this.unicornPhotoPreviewUrl ? html`
                                <label class="label">Dieses Foto Ihres Einhorns wird übermittelt:</label>
                            ` : html`
                                <label class="label">Bitte fügen Sie ein aktuelles Foto Ihres Einhorns hinzu *</label>
                            `}
                            ${this.unicornPhotoPreviewUrl ? html`
                                <img class="object-contain w-60" src=${this.unicornPhotoPreviewUrl}
                                     alt="Preview des Einhorn-Fotos">
                            ` : nothing}
                        </fieldset>
                    </div>
                    <h4>Weitere Angaben</h4>
                    <fieldset class="fieldset">
                        <div class="join join-vertical">
                            <textarea class="textarea"
                                      @change=${this.updateData.bind(this, 'unicornFreeText')}
                                      .value=${this.data.unicornFreeText || nothing}
                                      rows="5"
                                      placeholder="(Freitext)"
                            ></textarea>
                            <input @change=${this.updateData.bind(this, 'unicornRelatedPDF')} type="file"
                                   accept=".pdf,application/pdf" class="file-input"/>
                            <label class="label">Hier können Sie ein PDF Dokument hinzufügen (optional)</label>
                        </div>
                    </fieldset>
                </div>
                <button @click=${this.submitDataToBackend}
                        ?disabled=${this.dataSubmissionInProgress || this.dataSubmitted || (!this.dataComplete)}
                        class="btn btn-block glass my-5">
                        ${
                            this.dataSubmissionInProgress
                                ? 'Ihre Deklarationsdaten werden übermittelt ...'
                                : this.dataSubmitted
                                    ? 'Vielen Dank! Ihre Deklarationsdaten wurden übermittelt.'
                                    : this.dataComplete
                                        ? 'Deklarationsdaten übermitteln'
                                        : '(Deklarationsdaten noch unvollständig)'
                        }
                </button>
            </div>
        `;
    }
}
declare global {
    interface HTMLElementTagNameMap {
        'unicorn-element' : BundidAntrag;
    }
}
